import type { EpcScheduleTaskRow } from '../../types/scheduleTask';

/**
 * Microsoft Project-style compact duration label.
 * Input examples: `"10 days"`, `"1 day?"`, `"160 hrs"`, `"16h"`, `"8 edays"`, `10`, `"0d"`.
 * Output examples: `"10d"`, `"1d?"`, `"20d"` (160 h → 20 d), `"2d"` (16h), `"8ed"`, `"10d"`, `"0d"`.
 * Returns `""` when not recognisable.
 */
export function formatDurationCompact(raw: unknown): string {
  if (raw == null) return '';
  const sRaw = String(raw).trim();
  if (!sRaw) return '';

  const estimated = /\?$/.test(sRaw);
  const s = sRaw.replace(/\?$/, '').trim();

  const dayMatch = /^([+-]?\d+(?:\.\d+)?)\s*(edays?|days?|d)$/i.exec(s);
  if (dayMatch) {
    const n = parseFloat(dayMatch[1]!);
    const unit = /^e/i.test(dayMatch[2]!) ? 'ed' : 'd';
    return `${formatNumber(n)}${unit}${estimated ? '?' : ''}`;
  }

  const hourMatch = /^([+-]?\d+(?:\.\d+)?)\s*(hrs?|hours?|h)$/i.exec(s);
  if (hourMatch) {
    const hours = parseFloat(hourMatch[1]!);
    const days = hours / 8;
    const rounded = Math.round(days * 100) / 100;
    return `${formatNumber(rounded)}d${estimated ? '?' : ''}`;
  }

  const weekMatch = /^([+-]?\d+(?:\.\d+)?)\s*(wks?|weeks?|w)$/i.exec(s);
  if (weekMatch) {
    const n = parseFloat(weekMatch[1]!);
    return `${formatNumber(n)}w${estimated ? '?' : ''}`;
  }

  const monthMatch = /^([+-]?\d+(?:\.\d+)?)\s*(mons?|months?|mo)$/i.exec(s);
  if (monthMatch) {
    const n = parseFloat(monthMatch[1]!);
    return `${formatNumber(n)}mo${estimated ? '?' : ''}`;
  }

  const bareNumber = /^[+-]?\d+(?:\.\d+)?$/.test(s);
  if (bareNumber) {
    return `${s}d${estimated ? '?' : ''}`;
  }

  return sRaw;
}

function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return '';
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
}

/**
 * Normalised percent complete in range [0,100], or null if missing/invalid.
 * Accepts 0–1 fractional or 0–100 or "50%" string.
 */
export function parsePercentComplete(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim().replace(/%\s*$/, '');
  if (!s) return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  const pct = n > 0 && n <= 1 ? n * 100 : n;
  if (pct < 0) return 0;
  if (pct > 100) return 100;
  return Math.round(pct);
}

export function isTaskMilestone(t: EpcScheduleTaskRow): boolean {
  const m = t.milestone;
  return m === true || m === 'true' || m === 1 || m === '1';
}
